/**
 * VisualSelectTrigger Component
 *
 * Button trigger for visual option selection.
 * Shows current selection thumbnail + label, opens OptionPickerDialog on click.
 */

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IVisualSelectTriggerProps } from '@/@types/photostudio';
import { getOptionThumbnailPath, getPlaceholderThumbnailPath } from '@/@types/photostudio';

export function VisualSelectTrigger({
  value,
  placeholder,
  onClick,
  disabled = false,
  category,
}: IVisualSelectTriggerProps) {
  const [imageError, setImageError] = useState(false);

  const thumbnailPath = value
    ? imageError
      ? getPlaceholderThumbnailPath(category)
      : getOptionThumbnailPath(category, value)
    : null;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex w-full items-center gap-3 rounded-md border px-3 py-2',
        'bg-background text-left transition-colors',
        'border-input hover:bg-accent hover:text-accent-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'min-h-[40px]'
      )}
    >
      {/* Thumbnail preview */}
      {value && thumbnailPath && (
        <div className="h-8 w-12 flex-shrink-0 overflow-hidden rounded bg-muted">
          <img
            src={thumbnailPath}
            alt=""
            className="h-full w-full object-cover"
            onError={() => setImageError(true)}
            loading="lazy"
          />
        </div>
      )}

      {/* Value or placeholder */}
      <span className={cn('flex-1 truncate text-sm', !value && 'text-muted-foreground')}>
        {value || placeholder}
      </span>

      {/* Chevron */}
      <ChevronDown className="h-4 w-4 flex-shrink-0 opacity-50" />
    </button>
  );
}
